import {
	type BotMenu,
	DEFAULT_MENU_BACK_LABEL,
	DEFAULT_MENU_ROOT_TEXT,
	type MenuItem,
	type MenuItemAction,
	menuFormat,
} from "@velizhanin-dev/bot-scenario"
import { LayoutGrid, Plus } from "lucide-react"
import { toast } from "sonner"
import { useEffect, useState } from "react"
import { makeButtonId } from "@/entities/bot-builder"
import {
	addItem,
	ButtonStylePicker,
	type ItemForm,
	MenuButtonCard,
	MenuFormatPicker,
	MenuTree,
	mapItems,
	moveItem,
	placeholderChild,
	removeItem,
	WELCOME_NOTE,
	WelcomeMessage,
} from "@/features/edit-bot-menu"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@/shared/ui/sheet"
import { RichTextEditor } from "@/shared/ui/rich-text-editor"
import {
	type LoadedDraft,
	MainMenuHelp,
	WithDraft,
	WorkspaceSection,
} from "@/widgets/bot-workspace"
import { AnimatePresence, motion } from "framer-motion"
import { listItem } from "@/shared/lib/motion"
import { LoadSwap } from "@/shared/ui/load-swap"

const ACTION_LABELS: Record<MenuItemAction, string> = {
	flow: "Воронка",
	text: "Текст",
	submenu: "Подменю",
	url: "Ссылка",
	web_app: "Мини-приложение",
}

const ACTION_OPTIONS: { value: MenuItemAction; label: string; inlineOnly?: boolean }[] = [
	{ value: "flow", label: "Запускает воронку" },
	{ value: "text", label: "Отвечает текстом" },
	{ value: "submenu", label: "Открывает подменю" },
	{ value: "web_app", label: "Открывает мини-приложение" },
	{ value: "url", label: "Открывает ссылку", inlineOnly: true },
]

const isHttps = (value: string) => /^https:\/\/\S+\.\S+/.test(value.trim())
const isLink = (value: string) => isHttps(value) || /^tg:\/\/\S+/.test(value.trim())

function hasLinks(items: MenuItem[]): boolean {
	return items.some((item) => item.action === "url" || hasLinks(item.items ?? []))
}

/** «Конструктор → Главное меню»: приветствие и кнопки меню с подменю, как в Botmaker. */
export function MainMenuPage() {
	return (
		<WithDraft title="Главное меню" subtitle="Первое, что увидит пользователь, зайдя в бота">
			{(state) => <MainMenuEditor state={state} />}
		</WithDraft>
	)
}

function MainMenuEditor({ state }: { state: LoadedDraft }) {
	const { draft, actions } = state
	const { confirm } = useConfirm()
	const menu = draft.menu ?? null
	const [form, setForm] = useState<ItemForm | null>(null)
	const [backLabel, setBackLabel] = useState(menu?.backLabel ?? DEFAULT_MENU_BACK_LABEL)
	const [rootText, setRootText] = useState(menu?.rootText ?? DEFAULT_MENU_ROOT_TEXT)
	useEffect(() => {
		setBackLabel(menu?.backLabel ?? DEFAULT_MENU_BACK_LABEL)
		setRootText(menu?.rootText ?? DEFAULT_MENU_ROOT_TEXT)
	}, [menu?.backLabel, menu?.rootText])

	const mainFlow = draft.flows.find((flow) => flow.kind === "MAIN")
	const welcome = draft.nodes.filter(
		(node) => node.flowId === mainFlow?.id && node.type === "message" && node.note === WELCOME_NOTE,
	)
	const flows = draft.flows.filter((flow) => flow.kind !== "FALLBACK")
	const flowName = (flowId?: string) =>
		draft.flows.find((flow) => flow.id === flowId)?.name ?? "удалённая воронка"

	const save = (next: BotMenu | null) => void actions.saveMenu(next)
	const saveItems = (items: MenuItem[]) => {
		if (items.length === 0) {
			confirm({
				title: "Удалить последний пункт?",
				message: "Меню пропадёт: у пользователей исчезнет постоянная клавиатура.",
				confirmText: "Убрать меню",
				onConfirm: async () => {
					await actions.saveMenu(null)
				},
			})
			return
		}
		save({
			...menu,
			items,
			columns: menu?.columns ?? 1,
			backLabel: menu?.backLabel ?? DEFAULT_MENU_BACK_LABEL,
			rootText: menu?.rootText ?? DEFAULT_MENU_ROOT_TEXT,
		})
	}

	const format = menu ? menuFormat(menu) : "keyboard"
	const changeFormat = (next: typeof format) => {
		if (!menu || next === format) return
		if (next === "keyboard" && hasLinks(menu.items)) {
			toast.error("На клавиатуре ссылки не открываются. Замените пункты-ссылки или удалите их.")
			return
		}
		save({ ...menu, format: next })
	}

	const submitForm = () => {
		if (!form?.label.trim()) return
		const items = menu?.items ?? []
		const build = (base?: MenuItem): MenuItem => {
			const item: MenuItem = {
				id: form.id ?? makeButtonId(),
				label: form.label.trim(),
				action: form.action,
			}
			if (form.style) item.style = form.style
			if (form.action === "url" || form.action === "web_app") item.url = form.url.trim()
			if (form.action === "flow") item.flowId = form.flowId
			if (form.action === "text" || form.action === "submenu") item.text = form.text
			if (form.action === "text" && base?.media) item.media = base.media
			if (form.action === "submenu")
				item.items = base?.items?.length ? base.items : [placeholderChild()]
			return item
		}
		const next = form.id
			? mapItems(items, form.id, (current) => build(current))
			: addItem(items, form.parentId, build())
		saveItems(next)
		setForm(null)
	}

	const openForm = (item: MenuItem | null, parentId: string | null) =>
		setForm({
			id: item?.id,
			parentId,
			label: item?.label ?? "",
			action: item?.action ?? "flow",
			flowId: item?.flowId ?? flows[0]?.id ?? "",
			text: item?.text ?? "",
			url: item?.url ?? "",
			style: item?.style ?? null,
		})

	const deleteItem = (item: MenuItem) =>
		confirm({
			title: `Удалить «${item.label}»?`,
			message: item.items?.length
				? "Вместе с пунктами подменю."
				: "Кнопка пропадёт с клавиатуры бота.",
			confirmText: "Удалить",
			onConfirm: async () => {
				const next = removeItem(menu?.items ?? [], item.id)
				if (next.length === 0) await actions.saveMenu(null)
				else saveItems(next)
			},
		})

	return (
		<div className="space-y-0">
			<WorkspaceSection
				title="Приветственные сообщения"
				subtitle="Сообщения, которые пользователь получит после первого входа в бота или прохождения основной воронки до самого конца"
				help={<MainMenuHelp />}
			>
				<LoadSwap loading={false} id={welcome.length === 0 ? "empty" : "list"}>
					{welcome.length === 0 ? (
						<EmptyState title="Приветствия нет">
							Сообщения после основной воронки настраиваются в самой воронке.
						</EmptyState>
					) : (
						<div>
							<AnimatePresence initial={false}>
								{welcome.map((node) => (
									<motion.div key={node.id} {...listItem}>
										<div className="pb-4">
											<WelcomeMessage node={node} actions={actions} />
										</div>
									</motion.div>
								))}
							</AnimatePresence>
						</div>
					)}
				</LoadSwap>
			</WorkspaceSection>

			<WorkspaceSection
				title="Кнопки главного меню"
				subtitle="Пункт запускает воронку, отвечает текстом, открывает подменю, ссылку или мини-приложение."
				actions={
					<>
						{menu ? (
							<Button
								variant="outline"
								className="rounded-xl"
								onClick={() =>
									confirm({
										title: "Убрать меню?",
										message: "У пользователей исчезнет постоянная клавиатура.",
										confirmText: "Убрать",
										onConfirm: async () => {
											await actions.saveMenu(null)
										},
									})
								}
							>
								Убрать меню
							</Button>
						) : null}
						<Button className="rounded-xl" onClick={() => openForm(null, null)}>
							<Plus />
							Добавить кнопку
						</Button>
					</>
				}
			>
				<LoadSwap loading={false} id={menu ? "menu" : "empty"}>
					{!menu ? (
						<EmptyState icon={LayoutGrid} title="Меню нет">
							Добавьте первую кнопку, и у пользователей появится постоянная клавиатура.
						</EmptyState>
					) : (
						<div className="space-y-5">
							<MenuFormatPicker value={format} onChange={changeFormat} />
							<MenuTree
								items={menu.items}
								depth={0}
								actionLabels={ACTION_LABELS}
								flowName={flowName}
								onMove={(id, delta) => saveItems(moveItem(menu?.items ?? [], id, delta))}
								onEdit={(item) => openForm(item, null)}
								onDelete={deleteItem}
								onAddChild={(parentId) => openForm(null, parentId)}
							/>
							<div className="grid gap-4 border-t border-border/60 pt-4 sm:grid-cols-3">
								<Field label="Кнопок в ряду" htmlFor="menu-columns">
									<NativeSelect
										id="menu-columns"
										value={String(menu.columns)}
										onChange={(event) => save({ ...menu, columns: Number(event.target.value) })}
									>
										{[1, 2, 3].map((value) => (
											<option key={value} value={value}>
												{value}
											</option>
										))}
									</NativeSelect>
								</Field>
								<Field label="Кнопка «Назад» в подменю" htmlFor="menu-back">
									<Input
										id="menu-back"
										placeholder="Назад"
										value={backLabel}
										onChange={(event) => setBackLabel(event.target.value)}
										onBlur={() => {
											if (backLabel.trim() && backLabel !== menu.backLabel)
												save({ ...menu, backLabel })
										}}
									/>
								</Field>
								<Field label="Текст при возврате в меню" htmlFor="menu-root">
									<Input
										id="menu-root"
										placeholder="Например, Выберите раздел"
										value={rootText}
										onChange={(event) => setRootText(event.target.value)}
										onBlur={() => {
											if (rootText.trim() && rootText !== menu.rootText) save({ ...menu, rootText })
										}}
									/>
								</Field>
							</div>
							<MenuButtonCard
								value={menu.menuButton}
								onChange={(menuButton) => {
									const { menuButton: _previous, ...rest } = menu
									save(menuButton ? { ...rest, menuButton } : rest)
								}}
							/>
						</div>
					)}
				</LoadSwap>
			</WorkspaceSection>

			<Sheet
				open={!!form}
				onOpenChange={(open) => {
					if (!open) setForm(null)
				}}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-md">
					<SheetHeader>
						<SheetTitle>{form?.id ? "Кнопка меню" : "Новая кнопка"}</SheetTitle>
						<SheetDescription>
							Подпись и цвет кнопки видны пользователю в чате с ботом.
						</SheetDescription>
					</SheetHeader>
					{form ? (
						<form
							className="space-y-4 px-4"
							onSubmit={(event) => {
								event.preventDefault()
								submitForm()
							}}
						>
							<Field label="Подпись" htmlFor="menu-item-label">
								<Input
									id="menu-item-label"
									placeholder="Например, О клубе"
									maxLength={64}
									value={form.label}
									onChange={(event) => setForm({ ...form, label: event.target.value })}
								/>
							</Field>
							<Field label="Что делает" htmlFor="menu-item-action">
								<NativeSelect
									id="menu-item-action"
									value={form.action}
									onChange={(event) =>
										setForm({ ...form, action: event.target.value as MenuItemAction })
									}
								>
									{ACTION_OPTIONS.filter(
										(option) =>
											!option.inlineOnly || format === "inline" || form.action === option.value,
									).map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</NativeSelect>
							</Field>
							{format === "keyboard" ? (
								<p className="-mt-2 text-xs text-muted-foreground">
									Ссылки открываются только кнопками под сообщением. Формат меню меняется над
									списком кнопок.
								</p>
							) : null}
							{form.action === "url" || form.action === "web_app" ? (
								<Field
									label={form.action === "url" ? "Ссылка" : "Адрес мини-приложения"}
									htmlFor="menu-item-url"
									hint={
										form.action === "url"
											? "https:// или tg://, например канал: https://t.me/prokmk"
											: "Только https://. Откроется поверх чата, как мини-апп клуба"
									}
								>
									<Input
										id="menu-item-url"
										inputMode="url"
										placeholder="https://"
										value={form.url}
										onChange={(event) => setForm({ ...form, url: event.target.value })}
									/>
								</Field>
							) : form.action === "flow" ? (
								<Field label="Воронка" htmlFor="menu-item-flow">
									<NativeSelect
										id="menu-item-flow"
										value={form.flowId}
										onChange={(event) => setForm({ ...form, flowId: event.target.value })}
									>
										{flows.map((flow) => (
											<option key={flow.id} value={flow.id}>
												{flow.name}
											</option>
										))}
									</NativeSelect>
								</Field>
							) : (
								<Field
									label={form.action === "text" ? "Ответ" : "Сообщение при открытии подменю"}
									htmlFor="menu-item-text"
								>
									<RichTextEditor
										id="menu-item-text"
										format="telegram-html"
										placeholder={
											form.action === "text"
												? "Что бот ответит на нажатие"
												: "Например, Выберите вопрос"
										}
										minHeight="10rem"
										value={form.text}
										onChange={(text) =>
											setForm((current) => (current ? { ...current, text } : current))
										}
									/>
								</Field>
							)}
							<Field
								label="Цвет кнопки"
								hint="Telegram покрасит кнопку. Выделите одну-две главные, не все"
							>
								<ButtonStylePicker
									value={form.style}
									onChange={(style) => setForm({ ...form, style })}
								/>
							</Field>
							<SheetFooter className="px-0">
								<Button
									type="submit"
									disabled={
										!form.label.trim() ||
										(form.action === "flow" && !form.flowId) ||
										(form.action === "text" && !form.text.trim()) ||
										(form.action === "url" && !isLink(form.url)) ||
										(form.action === "web_app" && !isHttps(form.url))
									}
								>
									Сохранить
								</Button>
							</SheetFooter>
						</form>
					) : null}
				</SheetContent>
			</Sheet>
		</div>
	)
}
